/**
 * Hall of Fame — `/hof`. The top five players by win rate (with a link to the
 * top 20), then human dice-luck records: single game and lifetime.
 *
 * Luck game records open the completed board, which anyone may view. Player
 * records and win-rate rows open the public profile.
 */

import React from 'react'
import { ScrollView, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useQuery } from '@tanstack/react-query'
import {
  HOF_MIN_GAMES,
  HOF_PREVIEW_COUNT,
  HOF_WIDE_PREVIEW_COUNT,
  HOF_MAX_WIDTH,
  diceLuckRecordsQuery,
  luckGameBox,
  luckPlayerBox,
  winRateColumns,
  winRateQuery,
} from '../../../lib/hallOfFame'
import { ScreenHeader } from '../../../components/ui/ScreenHeader'
import { HofSectionCard } from '../../../components/hof/HofSectionCard'
import { LuckRecordBox } from '../../../components/hof/LuckRecordBox'
import { WinRateRow } from '../../../components/hof/WinRateRow'
import { DiceLuckHelpModal } from '../../../components/hof/DiceLuckHelpModal'
import { HelpButton } from '../../../components/ui/HelpButton'
import { pageLayout } from '../../../lib/pageLayout'

export default function HallOfFameScreen() {
  const router = useRouter()
  const luck = useQuery(diceLuckRecordsQuery)
  const { width } = useWindowDimensions()
  const { inset, tier } = pageLayout(width, { maxWidth: HOF_MAX_WIDTH })
  // Tablet and desktop show the top 10 in two columns (1-5, 6-10); phones the top 5.
  const twoColumn = tier !== 'phone'
  const previewCount = twoColumn ? HOF_WIDE_PREVIEW_COUNT : HOF_PREVIEW_COUNT
  const winRate = useQuery(winRateQuery(previewCount))
  const records = luck.data
  const [showLuckHelp, setShowLuckHelp] = React.useState(false)

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <ScreenHeader title="Hall of Fame" icon="trophy" />
      <ScrollView contentContainerStyle={{ paddingHorizontal: inset, paddingVertical: 12, gap: 12 }}>
        <HofSectionCard
          title={`Top ${previewCount} · Win rate`}
          subtitle={`Ranked games · minimum ${HOF_MIN_GAMES} games`}
          isLoading={winRate.isLoading}
          error={winRate.error}
          onRetry={() => { void winRate.refetch() }}
          testID="hof-win-rate"
          footer={(winRate.data?.entries.length ?? 0) > 0 ? (
            <TouchableOpacity
              onPress={() => router.push('/(app)/hof/win-rate')}
              accessibilityLabel="View all players by win rate"
              accessibilityRole="button"
              testID="hof-view-more"
              className="self-end p-1"
            >
              <Text className="font-stencil text-[11px] uppercase text-blood">View all →</Text>
            </TouchableOpacity>
          ) : null}
        >
          {(winRate.data?.entries.length ?? 0) === 0 ? (
            <Text className="font-typed text-ink-soft italic text-xs">
              // no qualified players yet — finish some games
            </Text>
          ) : (
            <View testID="hof-win-rate-columns" className={twoColumn ? 'flex-row gap-3' : 'gap-2'}>
              {winRateColumns(winRate.data!.entries, twoColumn ? 2 : 1).map((column, i) => (
                <View key={i} className="gap-2" style={twoColumn ? { flex: 1, minWidth: 0 } : undefined}>
                  {column.map((entry) => <WinRateRow key={entry.id} entry={entry} />)}
                </View>
              ))}
            </View>
          )}
        </HofSectionCard>

        <HofSectionCard
          title="Dice luck"
          subtitle={records
            ? `Ranked games · ${records.minRolls}+ rolls`
            : undefined}
          isLoading={luck.isLoading}
          error={luck.error}
          onRetry={() => { void luck.refetch() }}
          testID="hof-dice-luck"
          headerAccessory={(
            <HelpButton
              testID="hof-dice-luck-help-button"
              onPress={() => setShowLuckHelp(true)}
              accessibilityLabel="How dice luck works"
            />
          )}
        >
          <View className="gap-2">
            <View className="flex-row gap-2">
              <LuckRecordBox
                emptyTitle="Luckiest game"
                model={records?.luckiestGame ? luckGameBox(records.luckiestGame, 'lucky') : null}
              />
              <LuckRecordBox
                emptyTitle="Unluckiest game"
                model={records?.unluckiestGame ? luckGameBox(records.unluckiestGame, 'unlucky') : null}
              />
            </View>
            <View className="flex-row gap-2">
              <LuckRecordBox
                emptyTitle="Luckiest player"
                model={records?.luckiestPlayer ? luckPlayerBox(records.luckiestPlayer, 'lucky') : null}
              />
              <LuckRecordBox
                emptyTitle="Unluckiest player"
                model={records?.unluckiestPlayer ? luckPlayerBox(records.unluckiestPlayer, 'unlucky') : null}
              />
            </View>
          </View>
        </HofSectionCard>

      </ScrollView>
      <DiceLuckHelpModal visible={showLuckHelp} onClose={() => setShowLuckHelp(false)} />
    </SafeAreaView>
  )
}
